import {
  publishTimetableResultSchema,
  timetableEditorSchema,
  type TimetableMonthBody,
  type PublishTimetableResult,
  type TimetableEditor,
} from "@teamna/shared";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { api } from "../api/client.ts";
import { businessScheduleQueries, queryKeys } from "./keys.ts";

/**
 * The owner timetable (prototype `teamna/prototypes/owner-timetable.html`), one month at a time
 * (spec `teamna/strategy/timetable-months.md`): the week editor reads one month, asks the server what
 * its draft would do (a dry run), and publishes the month in one call.
 */

const base = (businessId: string) => `/business/businesses/${businessId}/timetable`;

/** GET /business/businesses/:id/timetable/editor?month=yyyy-MM. No month: this month. */
export function useTimetableEditor(businessId: string | null, month: string | null = null): UseQueryResult<TimetableEditor> {
  return useQuery({
    queryKey: queryKeys.setup.timetableEditor(businessId ?? "none", month ?? "now"),
    queryFn: () => api.get(`${base(businessId ?? "")}/editor${month ? `?month=${month}` : ""}`, timetableEditorSchema),
    enabled: Boolean(businessId),
  });
}

/**
 * What the month's draft would do to booked people: `POST .../timetable/months/:month/preview`, the
 * publish rolled back. Off while `request` is null (no changes). The last answer stays on screen
 * while the next one loads, so the bar does not blink on every drag.
 */
export function useTimetablePreview(
  businessId: string | null,
  month: string,
  request: TimetableMonthBody | null,
): UseQueryResult<PublishTimetableResult> {
  const key = request ? JSON.stringify(request) : "none";
  return useQuery({
    queryKey: queryKeys.setup.timetablePreview(businessId ?? "none", `${month}|${key}`),
    queryFn: () => api.post(`${base(businessId ?? "")}/months/${month}/preview`, request, publishTimetableResultSchema),
    enabled: Boolean(businessId) && request !== null,
    placeholderData: keepPreviousData,
    staleTime: 15_000,
  });
}

/**
 * POST /business/businesses/:id/timetable/months/:month/publish. The caller holds the key for as long
 * as the draft is the same: a second press after a lost answer must send the same key, so the server
 * replays the first publish instead of running it again. Every list that shows a session refreshes.
 */
export function usePublishTimetable(
  businessId: string,
  month: string,
): UseMutationResult<PublishTimetableResult, unknown, { request: TimetableMonthBody; idempotencyKey: string }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ request, idempotencyKey }) =>
      api.post(`${base(businessId)}/months/${month}/publish`, request, publishTimetableResultSchema, {
        idempotencyKey,
        timeoutMs: 90_000,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.setup.all(businessId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.owner.groups(businessId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.owner.templates(businessId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.owner.sessionsAll });
      void queryClient.invalidateQueries(businessScheduleQueries);
      void queryClient.invalidateQueries({ queryKey: queryKeys.owner.attendeesAll });
      void queryClient.invalidateQueries({ queryKey: queryKeys.scheduleAll });
      void queryClient.invalidateQueries({ queryKey: queryKeys.memberHomeAll });
      void queryClient.invalidateQueries({ queryKey: queryKeys.staff.all });
    },
  });
}
